<template>
    <ui-container class="py-8" v-if="this.form">
        <ui-header :title="editMode ? 'Editing Toolbox Talk - '+this.item.formattedDate : 'Create new Toolbox Talk'"
                   :subtitle="editMode ? 'ID #'+this.item.id : null">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="finished()" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <label class="my-0 py-2 mt-4">Load Toolbox Talk from Library</label>
        <div class="grid gap-6 grid-cols-12 py-0">
            <ui-select-searchable
                class="xl:col-span-12"
                v-model="form.toolbox_talk"
                :error="form.errors.get('toolboxtalk')"
                :options="toolboxTalkDropdown"
                @input="toolboxTalkChanged"
            ></ui-select-searchable>
        </div>
        <label class="my-0 py-2 mt-4">Toolbox Talk</label>
        <div class="grid gap-6 grid-cols-12 py-0">
            <ui-date-picker label="Talk Date" name="date" v-model="form.date" class="xl:col-span-6"
                            placeholder="What is date for this toolbox-talks?"
                            :error="form.errors.get('date')"
                            @input="save"></ui-date-picker>
            <ui-input label="Talk Reference" name="reference" v-model="form.reference"
                      placeholder="Toolbox Talk reference"
                      ref="reference"
                      class="xl:col-span-6" :error="form.errors.get('reference')"
                      @input="save"></ui-input>

            <ui-input label="Talk Title" name="title" v-model="form.title"
                      placeholder="Title of toolbox talk..."
                      ref="title"
                      class="xl:col-span-12" :error="form.errors.get('title')" @input="save"></ui-input>

            <ui-textarea autoresize label="Talk" name="talk" v-model="form.talk"
                         placeholder="Talk data in here...."
                         ref="talk"
                         class="xl:col-span-12" :error="form.errors.get('talk')"
                         @input="save"></ui-textarea>

        </div>
        <label class="my-0 py-0 mt-4">Attendees</label>
        <div
            class="flex mt-1.5 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold draggable">
            <div class="w-6 flex-shrink-0"></div>
            <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Name</div>
            <div class="flex-1 border-r border-gray-200 px-2 py-1">Position</div>
            <div class="w-6 flex-shrink-0 ml-2">
            </div>
        </div>
        <draggable :list="currentAttendees" draggable=".draggable" handle=".handle" group="attendees" @change="save">
            <toolbox-talk-attendee v-for="attendee in currentAttendees"
                                      :key="attendee.id"
                                      :attendee="attendee"
                                      ref="attendees"
                                      :toolbox-talk="toolboxTalk"
                                      :contacts="contacts"
                                      @updatedAttendee="save"
                                      @removeAttendee="removeAttendee"
            ></toolbox-talk-attendee>
        </draggable>
        <!-- New Task Item. -->
        <div class="flex border-b -mb-px border-gray-200">
            <div class="w-6 flex-shrink-0">
                <div class="px-2 py-2 w-5 h-5 flex attendees-center">
                    <i class="fa fa-plus text-xs text-gray-300"></i>
                </div>
            </div>
            <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                <textarea
                    placeholder="Add a new attendee..."
                    class="px-2 py-1 b-0 border-0 hover:z-10 focus:z-20 resize-none text-sm m-0 border-none shadow-none relative"
                    type="text"
                    rows="1"
                    v-model="newAttendee.name"
                    @input="addAttendeeLine"
                ></textarea>
            </div>
            <div class="w-6 ml-2 mt-1">
            </div>
        </div>
        <div class="grid gap-6 grid-cols-12 py-6">
            <package-contact-search
                class="col-span-12 xl:col-span-6"
                #default="{ open, query, setQuery }"
                @selected:contact="givenBySelected"
                :top="true"
                :contacts="contacts"
            >
                <ui-input label="Given By" name="givenByName" v-model="form.givenByName" placeholder=""
                          class="w-full"
                          @input="setQuery(form.givenByName);open();save()"
                          @focus="setQuery(form.givenByName);open();"
                ></ui-input>
            </package-contact-search>
            <ui-input label="Position" name="givenByPosition" v-model="form.givenByPosition" placeholder=""
                      class="col-span-12 xl:col-span-6" @input="save"></ui-input>
        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new v-if="editMode" @click="edit(false)" class="ml-3">Cancel</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid class="mt-6" v-if="editMode">
            <ui-data-grid-item label="Created By">{{ toolboxTalk.createdBy }} on {{
                    toolboxTalk.createdOn
                }}
            </ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="toolboxTalk.lastUpdatedBy">{{
                    toolboxTalk.lastUpdatedBy
                }} on {{
                    toolboxTalk.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>

</template>

<script>
import {ToolboxTalkLibraryItem, ToolboxTalk} from '@models'
import UiInput from "../../ui/UiInput.vue";
import draggable from 'vuedraggable'
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import PackageContactSearch from "../../contacts/PackageContactSearch.vue";
import UiTextarea from "../../ui/UiTextarea.vue";
import UiHeader from "../../ui/UiHeader.vue";
import {uuid} from "../../../utils";
import moment from "moment";
import ToolboxTalkAttendee from "./ToolboxTalkAttendee.vue";

export default {
    components: {
        ToolboxTalkAttendee,
        UiHeader,
        UiTextarea,
        PackageContactSearch,
        UiDropdown,
        UiDropdownItem,
        UiDataGridItem,
        UiDataGrid,
        AutoresizableTextarea,
        UiButtonNew,
        UiDatePicker,
        UiContainer,
        draggable,
        UiInput,

    },
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        contacts: Object,
        packageReference: null,
        packageName: null,
        toolboxTalkLibraryItems: Array,
    },
    data() {
        const initialData = this.item ? this.item.getFormAttributes() : {
            date: moment().format('YYYY-MM-DD'),
            attendees: []
        };
        return {
            currentAttendees: [],
            newAttendee: {
                name: null
            },
            toolboxTalk: null,
            form: null,
            errors: {
                date: null
            },
            saving: false,
            saveAgain: false,
        }
    },
    methods: {
        addAttendeeLine() {
            if (!this.newAttendee.name) return
            this.currentAttendees.push({
                id: uuid(),
                name: this.newAttendee.name,
                position: '',
            });
            this.save();
            this.newAttendee.name = ''
            this.$nextTick(() => {
                const inputField = this.$refs.attendees[this.$refs.attendees.length - 1].$refs.name
                let val = inputField.value;
                inputField.focus();
                inputField.value = val;
            })
        },
        removeAttendee(e) {
            this.currentAttendees = this.currentAttendees.filter(attendee => attendee.id !== e.id);
            this.save();
        },
        save() {
            if (this.editMode) {
                if (this.saving) {
                    this.saveAgain = true;
                }
                this.saving = true
                this.form.submit()
            }
        },
        finished() {
            let sorter = (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            };
            this.edit(false, sorter)
        },
        givenBySelected(e) {
            this.form.givenByName = e.name
            this.save();
        },
        toolboxTalkChanged() {
            if (this.form.toolbox_talk) {
                let talk = this.toolboxTalkLibraryItems.find(el => el.id === this.form.toolbox_talk.value);
                if (talk) {
                    if (this.editMode || this.updated) {
                        const title = 'Are you sure you load the toolbox talk data from the library?'
                        const description = 'You are loading toolbox talk ' + talk.reference + ' - ' + talk.title + ' from the library. This will override any changes you have made to this current toolbox talk. Are you sure you want to do this?'
                        eventBus.$emit('warning-modal-open', title, description, 'Load from Library', async () => {
                            this.setFromLibraryItem(talk)
                        });
                    } else {
                        this.setFromLibraryItem(talk)
                    }
                }
            }
        },
        setFromLibraryItem(talk) {
            this.form.reference = talk.reference;
            this.form.title = talk.title;
            this.form.talk = talk.talk;
            this.$nextTick(() => {
                // trigger input event to force resize
                this.$refs.talk.$children[0].$children[0].$el.dispatchEvent(new Event('input'));
                this.$nextTick(() => {
                    this.updated = false;
                });
            });
        },
    },
    computed: {
        editMode() {
            return !!this.item
        },
        toolboxTalkDropdown() {
            return this.toolboxTalkLibraryItems.map(x => x.toDropdown());
        }
    },
    async mounted() {
        if (this.item) {
            this.toolboxTalk = await ToolboxTalk.find(this.item.packageReference, this.item.id)
            this.currentAttendees = this.toolboxTalk.attendees
        } else {
            this.toolboxTalk = {date: moment().format('YYYY-MM-DD'), attendees: []}
        }
        this.form = new Form(this.editMode ? this.toolboxTalk.getFormAttributes() : this.toolboxTalk, async (form) => {
            this.saving = true;
            do {
                this.saveAgain = false
                let data = form.export();
                if (!data.date) {
                    this.errors.date = "The date field is required."
                    return;
                }
                data.packageReference = this.packageReference;
                data.packageName = this.packageName;
                data.attendees = this.currentAttendees ? this.currentAttendees : [];
                data.attendees.map((p, i) => p.order = i);
                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };
                if (this.editMode) {
                    await this.toolboxTalk.update(data).then(() =>
                        Object.assign(this.item, this.toolboxTalk)
                    )
                    this.edit(true, sorter)
                } else {
                    const induction = await ToolboxTalk.create(this.packageReference, uuid(), data)

                    this.addItem(induction, 0, sorter);
                    form.reset()
                }
            } while (this.saveAgain)
            this.saving = false;
        })
    }
}
</script>
